import {
  WorkforceDashboardAlignmentShape,
  WorkforceDashboardAlignmentShapeScores,
  WorkforceDashboardMetric,
  WorkforceDashboardMetricEnvelope,
  WorkforceDashboardRoleCoverageAverageScores,
  WorkforceDashboardRoleCoverageRow,
} from '../types/workforceDashboardTypes';

import {ApiSerializer} from '../../../../serializers/ApiSerializer';

type DashboardMetricResponse<TData> = {
  workforceDashboard?: {
    metric?: WorkforceDashboardMetric;
    data?: TData;
  };
};

// BE role_coverage_heatmap / alignment_shape metrics still emit legacy v1 field names
// (no dedicated soft-skills average yet); map them onto the v2 dimension names.
interface ApiAlignmentShapeScores {
  coreCompetencies?: number | null;
  hardSkills?: number | null;
  tools?: number | null;
  domainKnowledge?: number | null;
  softSkills?: number | null;
  responsibilities?: number | null;
  competencies?: number | null;
  knowledge?: number | null;
}

interface ApiRoleCoverageAverageScores {
  overall?: number | null;
  coreCompetencies?: number | null;
  hardSkills?: number | null;
  tools?: number | null;
  domainKnowledge?: number | null;
  softSkills?: number | null;
  responsibilities?: number | null;
  skillsCompetencies?: number | null;
  knowledgeSkills?: number | null;
}

const normalizeAlignmentShapeScores = (
  raw: ApiAlignmentShapeScores | undefined
): WorkforceDashboardAlignmentShapeScores => ({
  coreCompetencies: raw?.coreCompetencies ?? raw?.competencies ?? null,
  hardSkills: raw?.hardSkills ?? raw?.knowledge ?? null,
  tools: raw?.tools ?? null,
  domainKnowledge: raw?.domainKnowledge ?? null,
  softSkills: raw?.softSkills ?? null,
  responsibilities: raw?.responsibilities ?? null,
});

const normalizeAlignmentShapeData = (raw: unknown): WorkforceDashboardAlignmentShape => {
  const body = raw as {companyAverage?: ApiAlignmentShapeScores; roles?: Array<Record<string, unknown>>};

  return {
    companyAverage: normalizeAlignmentShapeScores(body?.companyAverage),
    roles: (body?.roles ?? []).map((role) => ({
      targetProfileTitle: String(role.targetProfileTitle ?? ''),
      profileCount: Number(role.profileCount ?? 0),
      scores: normalizeAlignmentShapeScores(role.scores as ApiAlignmentShapeScores | undefined),
    })),
  };
};

const normalizeRoleCoverageAverageScores = (
  raw: ApiRoleCoverageAverageScores | undefined
): WorkforceDashboardRoleCoverageAverageScores => ({
  overall: raw?.overall ?? null,
  coreCompetencies: raw?.coreCompetencies ?? raw?.skillsCompetencies ?? null,
  hardSkills: raw?.hardSkills ?? raw?.knowledgeSkills ?? null,
  tools: raw?.tools ?? null,
  domainKnowledge: raw?.domainKnowledge ?? null,
  softSkills: raw?.softSkills ?? null,
  responsibilities: raw?.responsibilities ?? null,
});

const normalizeRoleCoverageData = (raw: unknown): WorkforceDashboardRoleCoverageRow[] => {
  const rows = (raw as Array<Record<string, unknown>>) ?? [];

  return rows.map((row) => ({
    targetProfileTitle: String(row.targetProfileTitle ?? ''),
    profileCount: Number(row.profileCount ?? 0),
    averageScores: normalizeRoleCoverageAverageScores(row.averageScores as ApiRoleCoverageAverageScores | undefined),
  }));
};

const normalizeMetricData = (metric: WorkforceDashboardMetric, data: unknown): unknown => {
  if (metric === 'alignment_shape') {
    return normalizeAlignmentShapeData(data);
  }

  if (metric === 'role_coverage_heatmap') {
    return normalizeRoleCoverageData(data);
  }

  return data;
};

export const mapWorkforceDashboardMetricResponse = <TData>(raw: unknown): WorkforceDashboardMetricEnvelope<TData> => {
  const body = ApiSerializer.toCamelCase(raw) as DashboardMetricResponse<TData>;
  const metric = body.workforceDashboard?.metric ?? 'overview';
  const data = normalizeMetricData(metric, body.workforceDashboard?.data) as TData;

  return {metric, data};
};
